import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Money, NativeSelect, StatusPill } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", unit_label: "1 kg pack", pack_kg: 1, price: 0, gst_rate: 0, recipe: [], active: true, shelf_life_days: 4 };

function ProductDialog({ prod, materials, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (prod) setF({ ...EMPTY, ...prod, recipe: (prod.recipe || []).map((r) => ({ material_id: r.material_id, qty: r.qty })) }); }, [prod]);
  const updR = (i, k, v) => setF({ ...f, recipe: f.recipe.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const save = async () => {
    try {
      const body = { ...f, price: +f.price, gst_rate: +f.gst_rate, pack_kg: +f.pack_kg, shelf_life_days: +(f.shelf_life_days ?? 4), recipe: f.recipe.filter((r) => r.material_id && +r.qty > 0).map((r) => ({ material_id: r.material_id, qty: +r.qty })) };
      prod.id ? await api.put(`/products/${prod.id}`, body) : await api.post("/products", body);
      toast.success("Product saved");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label, type = "text") => (
    <div className="space-y-1"><Label>{label}</Label><Input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`product-${k}-input`} /></div>
  );
  return (
    <Dialog open={!!prod} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-display">{prod?.id ? "Edit" : "New"} product</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{field("name", "Product name")}</div>
          {field("unit_label", "Pack label")}
          {field("price", "Selling price (₹)", "number")}
          {field("gst_rate", "GST %", "number")}
          {field("hsn", "HSN code")}
          {field("pack_kg", "Pack weight (kg)", "number")}
          {field("shelf_life_days", "Shelf life (days, refrigerated)", "number")}
        </div>
        <div>
          <Label>Recipe per pack (raw materials)</Label>
          <div className="mt-2 space-y-2">
            {f.recipe.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_110px_36px] gap-2">
                <NativeSelect value={r.material_id} onChange={(e) => updR(i, "material_id", e.target.value)} data-testid={`recipe-material-${i}`}>
                  {materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
                </NativeSelect>
                <Input type="number" step="0.001" value={r.qty} onChange={(e) => updR(i, "qty", e.target.value)} data-testid={`recipe-qty-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => setF({ ...f, recipe: f.recipe.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setF({ ...f, recipe: [...f.recipe, { material_id: materials[0]?.id, qty: "" }] })} data-testid="recipe-add-row"><Plus className="mr-1 h-3 w-3" />Add ingredient</Button>
          </div>
        </div>
        <DialogFooter><Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={save} data-testid="product-save-btn">Save product</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/materials").then((r) => setMaterials(r.data)); }, []);
  const remove = async (p) => { await api.delete(`/products/${p.id}`); toast.success("Product deactivated"); load(); };

  return (
    <div data-testid="products-page">
      <PageHeader eyebrow="Products & recipes" title="Know your cost per pack" subtitle="Recipe cost uses the live average purchase price of each raw material.">
        <Button className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => setEdit({})} data-testid="add-product-btn"><Plus className="mr-1 h-4 w-4" />New product</Button>
      </PageHeader>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {products.map((p, idx) => (
          <div key={p.id} className={`rounded-3xl border border-[#E7E2D6] bg-white p-6 shadow-sm hover:shadow-lg transition-shadow bf-rise bf-d${(idx % 4) + 1} ${p.active ? "" : "opacity-50"}`} data-testid={`product-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-xl font-bold text-[#0F382C]">{p.name}</div>
                <div className="text-sm text-muted-foreground">{p.unit_label}{p.gst_rate ? ` · GST ${p.gst_rate}%` : ""}</div>
              </div>
              {!p.active && <StatusPill status="skipped" label="Inactive" />}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-5">
              <div className="rounded-xl bg-[#FAF4E8] p-3"><div className="text-[11px] uppercase text-muted-foreground">Price</div><Money value={p.price} className="font-semibold" /></div>
              <div className="rounded-xl bg-[#FAF4E8] p-3"><div className="text-[11px] uppercase text-muted-foreground">Cost</div><Money value={p.unit_cost} className="font-semibold" testid={`product-cost-${p.id}`} /></div>
              <div className="rounded-xl bg-emerald-50 p-3"><div className="text-[11px] uppercase text-emerald-700">Margin</div><span className="font-mono font-semibold text-emerald-800">{p.margin_pct}%</span></div>
            </div>
            <div className="mt-4 space-y-1 text-sm">
              {p.recipe.map((r) => (
                <div key={r.material_id} className="flex justify-between text-muted-foreground"><span>{r.name} · {r.qty} {r.unit}</span><Money value={r.cost} /></div>
              ))}
            </div>
            <div className="mt-5 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="mr-1 h-3 w-3" />Edit</Button>
              {p.active && <Button variant="ghost" size="sm" onClick={() => remove(p)} data-testid={`deactivate-product-${p.id}`}>Deactivate</Button>}
            </div>
          </div>
        ))}
      </div>
      <ProductDialog prod={edit} materials={materials} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
}
